import { isInMatch, MATCH_STATE } from '../machine';
import { ok, reject } from '../result';
import { freshHypothesisProblem } from './setup';
import type {
  AddDiscoveryHypothesisCommand,
  AddDiscoveryHypothesisSnapshot,
  Decision,
  DiscoveryOp,
} from './types';

/**
 * DISC-5 — adding a row mid-Discovery (build unit U-8).
 *
 * > *"An `＋` control in the Discovery top zone opens the template library and the PRE-4 stubs as a
 * > full-screen sheet. Selection is tap-only, so the flow is legal on an in-play surface (LIVE-9);
 * > free-text creation is **not** offered here (PRE-3). The new row is inserted into the page
 * > matching its `discoveryBucket` and, like every other order change, does not relocate any
 * > existing row until the quiet window elapses (DISC-7)."* — design §6.1
 *
 * ── `Decision<DiscoveryOp>` is the line that matters ─────────────────────────
 *
 * `DiscoveryOp` is `insertHypothesis` and nothing else. A row added during a half **cannot compile**
 * a `boardPosition` write or a lock, so INV-4's *"assigned exactly once per lock or per explicit
 * user swap"* keeps the two writers it already has, and the DISC-7 deferral engine never has to
 * reason about a row that appeared already positioned. The narrower type is doing the work that a
 * "remember not to set boardPosition" comment would otherwise be asked to do.
 *
 * ── LIVE-9 lives in the caller, and that is not a gap ────────────────────────
 *
 * *"No flow in an in-play state may require free-text entry to complete."* This handler takes a
 * finished row and cannot see how its text was produced, so the guarantee is the sheet's: it offers
 * the library and the PRE-4 pickers, and PRE-3's free-text control is absent from it. Asserting
 * "no text input exists in the DOM" is explicitly not sufficient (VER-6), so the browser pass drives
 * the whole add-a-row flow with taps alone instead.
 */

const MODULE = 'commands/discovery';

/**
 * DISC-5 — validate and insert one list-only hypothesis.
 *
 * The window is `capturePhase === 'discovery'` on a match that has not finished:
 *
 * - **`preMatch` is inside it.** PRE-7's *"Start in Discovery"* exit hands the user straight to this
 *   surface with the clock unstarted (§6.0, and U-7 records `discovery` as the resume surface), so
 *   refusing there would strand them on a screen whose `＋` does nothing.
 * - **`halftime` is inside it** through `isInMatch`, which is the same predicate LIVE-6's undo
 *   window uses. HT-2 owns the halftime *surface*; this is only about the phase remaining valid.
 * - **`fullTime` and everything after are outside it.** There is no capture surface left for the row
 *   to appear on, and a hypothesis created after Full Time would carry a zero count into
 *   distillation as if it had been watched for.
 *
 * `capturePhase === 'locked'` is refused rather than quietly accepted: a locked board's list-only
 * tail is reached through LOCK-6 and HT-2, which are different flows with different affordances
 * (U-9, U-13). Accepting here would create a row nothing on the locked board renders.
 */
export function handleAddDiscoveryHypothesis(
  cmd: AddDiscoveryHypothesisCommand,
  snap: AddDiscoveryHypothesisSnapshot,
): Decision<DiscoveryOp> {
  const match = snap.match;
  if (match === undefined) {
    return reject(MODULE, cmd.matchId, 'ERR_MATCH_NOT_FOUND', 'That match no longer exists.');
  }

  if (match.state !== MATCH_STATE.preMatch && !isInMatch(match.state)) {
    return reject(
      MODULE,
      cmd.matchId,
      'ERR_MATCH_NOT_CAPTURING',
      'This match has finished, so there is nothing left to add a hypothesis to.',
      { state: match.state },
    );
  }

  if (match.capturePhase !== 'discovery') {
    return reject(
      MODULE,
      cmd.matchId,
      'ERR_NOT_IN_DISCOVERY',
      'The board is locked. Add this from the board or at halftime instead.',
      { capturePhase: match.capturePhase },
    );
  }

  if (cmd.row.text.trim() === '') {
    return reject(
      MODULE,
      cmd.matchId,
      'ERR_HYPOTHESIS_TEXT_REQUIRED',
      'That hypothesis has no text.',
      { id: cmd.row.id },
    );
  }

  const problem = freshHypothesisProblem(cmd.row, cmd.matchId);
  if (problem !== null) {
    return reject(
      MODULE,
      cmd.matchId,
      'ERR_COMMAND_ROW_MISMATCH',
      'That row could not be added because it was malformed.',
      { id: cmd.row.id, problem },
    );
  }

  // Re-adding an id would overwrite a live row — including its count — through a `put`. The snapshot
  // is read inside the transaction, so this is a real check and not a race.
  if (snap.existingIds.includes(cmd.row.id)) {
    return reject(
      MODULE,
      cmd.matchId,
      'ERR_HYPOTHESIS_EXISTS',
      'That hypothesis is already on this match.',
      { id: cmd.row.id },
    );
  }

  return ok({
    ops: [{ t: 'insertHypothesis', row: cmd.row }],
    log: [
      {
        module: MODULE,
        level: 'info',
        matchId: cmd.matchId,
        msg: 'DISC-5: hypothesis added mid-Discovery',
        data: { id: cmd.row.id, discoveryBucket: cmd.row.discoveryBucket },
      },
    ],
    post: [],
  });
}
